import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { MapPin, Loader2, AlertCircle, CheckCircle2, Navigation } from 'lucide-react';
import useGeolocation from '../hooks/useGeolocation';

/**
 * Location Permission Dialog Component
 * Shows a prompt explaining why location is needed before requesting permission
 */
const LocationPermissionPrompt = ({ 
    onLocationCaptured, 
    context = 'registration', // 'registration' or 'profile'
    showSkip = true,
    className = ''
}) => {
    const [showPrompt, setShowPrompt] = useState(true);
    const { location, loading, error, permissionDenied, requestLocation } = useGeolocation();

    const contextMessages = {
        registration: {
            title: 'Share Your Location',
            description: 'We use your location to connect you with local tournaments, find nearby training facilities, and match you with scouts in your area.',
            benefits: [
                'Discover tournaments near you',
                'Connect with local coaches and scouts',
                'Join regional competitions',
                'Get personalized event recommendations'
            ]
        },
        profile: {
            title: 'Add Your Location',
            description: 'Adding your location helps scouts and coaches discover talented athletes in specific regions.',
            benefits: [
                'Increase visibility to scouts',
                'Get matched with regional teams',
                'Receive local event notifications',
                'Connect with nearby athletes'
            ]
        }
    };

    const messages = contextMessages[context] || contextMessages.registration;

    const handleAllowLocation = async () => {
        const locationData = await requestLocation();
        if (locationData) {
            onLocationCaptured(locationData);
            setShowPrompt(false);
        }
    };

    const handleSkip = () => {
        setShowPrompt(false);
        onLocationCaptured(null);
    };

    // Location already captured
    if (location && !showPrompt) {
        return (
            <div className={`bg-green-500/10 border border-green-500/30 p-4 ${className}`}>
                <div className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <div className="flex-1">
                        <p className="text-sm font-medium text-green-400">Location captured</p>
                        <p className="text-xs text-muted-foreground mt-1">
                            {location.city && location.country 
                                ? `${location.city}, ${location.country}`
                                : `${location.latitude.toFixed(4)}, ${location.longitude.toFixed(4)}`
                            }
                        </p>
                    </div>
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowPrompt(true)}
                        className="text-xs text-muted-foreground hover:text-white"
                    >
                        Update
                    </Button>
                </div>
            </div>
        );
    }

    // Permission denied state
    if (permissionDenied) {
        return (
            <div className={`bg-yellow-500/10 border border-yellow-500/30 p-4 ${className}`}>
                <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                        <p className="text-sm font-medium text-yellow-400">Location access denied</p>
                        <p className="text-xs text-muted-foreground mt-1">
                            To enable location, please update your browser settings and refresh the page.
                        </p>
                        {showSkip && (
                            <Button
                                type="button"
                                variant="link"
                                size="sm"
                                onClick={handleSkip}
                                className="text-xs text-primary p-0 h-auto mt-2"
                            >
                                Continue without location
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // Error state
    if (error && !permissionDenied) {
        return (
            <div className={`bg-red-500/10 border border-red-500/30 p-4 ${className}`}>
                <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                        <p className="text-sm font-medium text-red-400">Location error</p>
                        <p className="text-xs text-muted-foreground mt-1">{error}</p>
                        <div className="flex gap-2 mt-2">
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={handleAllowLocation}
                                className="text-xs border-white/10"
                            >
                                Try again
                            </Button>
                            {showSkip && (
                                <Button
                                    type="button"
                                    variant="link"
                                    size="sm"
                                    onClick={handleSkip}
                                    className="text-xs text-muted-foreground"
                                >
                                    Skip
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Show permission prompt
    return (
        <div className={`bg-[#0d2847] border border-white/10 p-5 ${className}`}>
            <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Navigation className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1">
                    <h3 className="font-heading font-bold text-lg uppercase tracking-tight mb-2">
                        {messages.title}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4">
                        {messages.description}
                    </p>
                    
                    <ul className="space-y-2 mb-5">
                        {messages.benefits.map((benefit, index) => (
                            <li key={index} className="flex items-center gap-2 text-sm text-muted-foreground">
                                <MapPin className="w-3 h-3 text-primary flex-shrink-0" />
                                {benefit}
                            </li>
                        ))}
                    </ul>
                    
                    <div className="flex gap-3">
                        <Button
                            type="button"
                            onClick={handleAllowLocation}
                            disabled={loading}
                            className="bg-primary hover:bg-cyan-400 text-[#0a1628] font-bold uppercase tracking-widest text-xs"
                            data-testid="allow-location-btn"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Getting Location...
                                </>
                            ) : (
                                <>
                                    <MapPin className="w-4 h-4 mr-2" />
                                    Allow Location
                                </>
                            )}
                        </Button>
                        {showSkip && (
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={handleSkip}
                                className="text-muted-foreground hover:text-white uppercase tracking-widest text-xs"
                                data-testid="skip-location-btn"
                            >
                                Not Now
                            </Button>
                        )}
                    </div>
                    
                    <p className="text-xs text-muted-foreground mt-4">
                        Your location data is stored securely and never shared without your consent.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default LocationPermissionPrompt;
